import RAPIER from '@dimforge/rapier3d-compat';
import type { Collider, RigidBody, World } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';

import { buildFallbackScoreRow } from './MazeVisuals';
import { buildScoreRowColliders } from '../physics/colliders/scoreRowColliders';

/**
 * The scoring row: seven pockets bolted to the cabinet below the board.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS A SEPARATE OBJECT AND NOT PART OF THE MAZE
 * ---------------------------------------------------------------------------
 * The maze is one kinematic body that rotates. The row must NOT rotate - the
 * board turns to aim, and the ball's exit angle decides the pocket. A row that
 * turned with the board would slide its pockets out from under the ball while
 * the ball was still falling, and the player's aim would stop corresponding to
 * anything.
 *
 * So this is a `fixed` body in world space, added straight to the scene rather
 * than under the maze group. Its geometry is already expressed in world
 * coordinates in the spec, so it needs no transform at all.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE IS NOT CALLED `ScoreRow.ts`
 * ---------------------------------------------------------------------------
 * `src/game/scoreRow.ts` already exists and holds the row's pure geometry and
 * `resolveSlot()`. On a case-insensitive filesystem - which is the default on
 * macOS - `ScoreRow.ts` and `scoreRow.ts` are THE SAME FILE, so the two modules
 * cannot be named that way. "Rig" is the project's existing word for a body
 * plus the visual it drives (`CameraRig`), so the pair reads
 * `scoreRow.ts` (the numbers) / `ScoreRowRig.ts` (the object).
 */
export class ScoreRowRig {
  /** Root transform the scene holds. Never moved after construction. */
  readonly group: THREE.Group;
  /** The row's visual: the loaded `scoring_row.glb`, or the procedural fallback. */
  readonly visual: THREE.Object3D;
  readonly body: RigidBody;
  readonly colliders: Collider[];
  /** True when the production model could not be used. */
  readonly usedFallback: boolean;

  constructor(world: World, visual?: THREE.Object3D) {
    this.usedFallback = !visual;

    this.group = new THREE.Group();
    this.group.name = 'scoring-row';
    this.visual = visual ?? buildFallbackScoreRow();
    this.group.add(this.visual);

    // A fixed body: no mass, no velocity, never integrated. It exists only to
    // own the colliders, which is the same pattern the maze uses for its own.
    this.body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
    this.colliders = buildScoreRowColliders(world, this.body);
  }
}
